import { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import type { SourceDetail } from '@/hooks/finance/useDreRealizado';
import { User, Receipt, FileText, TrendingUp, DollarSign } from 'lucide-react';

interface DreLineDetailPopoverProps {
  accountCode: string;
  accountName: string;
  month: string;
  monthLabel: string;
  amount: number;
  sourceDetails?: SourceDetail[];
  currency: string;
  children: React.ReactNode;
}

const formatCurrency = (value: number, currency: string = 'USD') => {
  return new Intl.NumberFormat(currency === 'BRL' ? 'pt-BR' : 'en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const getSourceIcon = (sourceType: SourceDetail['sourceType']) => {
  switch (sourceType) {
    case 'employee':
      return <User className="h-3.5 w-3.5" />;
    case 'recurring_expense':
      return <Receipt className="h-3.5 w-3.5" />;
    case 'budget':
      return <FileText className="h-3.5 w-3.5" />;
    case 'receivable':
      return <DollarSign className="h-3.5 w-3.5" />;
    case 'history':
      return <TrendingUp className="h-3.5 w-3.5" />;
    default:
      return <FileText className="h-3.5 w-3.5" />;
  }
};

const getSourceLabel = (sourceType: SourceDetail['sourceType']) => {
  switch (sourceType) {
    case 'employee':
      return 'Funcionário';
    case 'recurring_expense':
      return 'Despesa Recorrente';
    case 'budget':
      return 'Orçamento';
    case 'receivable':
      return 'Recebível';
    case 'history':
      return 'Histórico';
    default:
      return sourceType;
  }
};

const getSourceColor = (sourceType: SourceDetail['sourceType']) => {
  switch (sourceType) {
    case 'employee':
      return 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300';
    case 'recurring_expense':
      return 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300';
    case 'budget':
      return 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300';
    case 'receivable':
      return 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300';
    case 'history':
      return 'bg-gray-100 text-gray-700 dark:bg-gray-900 dark:text-gray-300';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

export function DreLineDetailPopover({
  accountCode,
  accountName,
  month,
  monthLabel,
  amount,
  sourceDetails,
  currency,
  children,
}: DreLineDetailPopoverProps) {
  const [open, setOpen] = useState(false);
  
  const hasDetails = sourceDetails && sourceDetails.length > 0;
  
  if (!hasDetails) {
    return <>{children}</>;
  }
  
  // Group by source type
  const groupedDetails = sourceDetails.reduce((acc, detail) => {
    if (!acc[detail.sourceType]) {
      acc[detail.sourceType] = [];
    }
    acc[detail.sourceType].push(detail);
    return acc;
  }, {} as Record<string, SourceDetail[]>);
  
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button 
          className="text-left cursor-pointer hover:underline underline-offset-2 decoration-dashed"
          onClick={(e) => {
            e.stopPropagation();
            setOpen(true);
          }}
        >
          {children}
        </button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-80 p-0" 
        align="start"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-3 border-b bg-muted/50">
          <div className="font-medium text-sm">{accountCode} - {accountName}</div>
          <div className="flex items-center justify-between mt-1">
            <span className="text-xs text-muted-foreground">{monthLabel}</span>
            <span className="font-semibold">{formatCurrency(amount, currency)}</span>
          </div>
        </div>
        
        <ScrollArea className="max-h-[300px]">
          <div className="p-2 space-y-3">
            {Object.entries(groupedDetails).map(([type, details]) => (
              <div key={type}>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Badge 
                    variant="secondary" 
                    className={cn("text-xs px-1.5 py-0", getSourceColor(type as SourceDetail['sourceType']))}
                  >
                    {getSourceIcon(type as SourceDetail['sourceType'])}
                    <span className="ml-1">{getSourceLabel(type as SourceDetail['sourceType'])}</span>
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    ({details.length})
                  </span>
                </div>
                
                <div className="space-y-1 pl-1">
                  {details.map((detail, idx) => (
                    <div 
                      key={`${detail.sourceId}-${idx}`}
                      className="flex items-center justify-between text-sm py-0.5 px-1.5 rounded hover:bg-muted/50"
                    >
                      <span className="truncate flex-1 text-muted-foreground">
                        {detail.sourceName}
                      </span>
                      <span className="font-medium ml-2 tabular-nums">
                        {formatCurrency(detail.amount, currency)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
        
        <div className="p-2 border-t bg-muted/30">
          <div className="flex items-center justify-between text-sm">
            <span className="font-medium">Total</span>
            <span className="font-bold">{formatCurrency(amount, currency)}</span>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}